import { RequestQuoteOutlinedIcon } from '@cmg/design-system';
import { xcSelectors } from '@cmg/e2e-selectors';
import { useMemo } from 'react';

import { useFeatureToggles } from '../../../common/config';
import { useCmgEntityKeyContext } from '../contexts/CmgEntityKeyContext';
import offeringSidePanelBuySideRouteFactory from '../offeringSidePanelBuySideRouteFactory';
import { useOfferingSidePanelContext } from '../order-book/indication-activity/context-old/OfferingSidePanelContext';
import { useOfferingSidePanelRouteMatch } from './useOfferingSidePanelMatch';
import { NavItemGroup } from './useSidePanelSellSideNavItems';

type useSidePanelBuySideOnPlatformOrderNavItemProps = {
  isFiledOffering: boolean;
  baseSidePanelUrl: string;
  isLinkedOffering: boolean;
};

export function useSidePanelBuySideOnPlatformOrderNavItem({
  isFiledOffering,
  baseSidePanelUrl,
  isLinkedOffering,
}: useSidePanelBuySideOnPlatformOrderNavItemProps) {
  const { indication } = useOfferingSidePanelContext();
  const { cmgEntityKey } = useCmgEntityKeyContext();
  const { isOdsFunctionalityEnabled } = useFeatureToggles();

  const matchesCommunicatedDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.orderBookIndicationActivity.routePath
  );

  const matchesInternalDemand = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.internalDemand.routePath
  );

  const matchesUpdateHistory = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.orderBookUpdateHistory.routePath
  );

  const matchesCompliance = useOfferingSidePanelRouteMatch(
    offeringSidePanelBuySideRouteFactory.orderBookCompliance.routePath
  );
  return useMemo(() => {
    const orderGroup: NavItemGroup = {
      id: 'order',
      label: 'Order',
      routePath: offeringSidePanelBuySideRouteFactory.orderBook.routePath,
      icon: <RequestQuoteOutlinedIcon />,
      testId: 'on-platform-order-book-link',
      href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.orderBook.getUrlPath(),
      items: [],
    };
    if (!isFiledOffering) {
      orderGroup.items.push({
        id: 'communicated-demand',
        label: 'Communicated Demand',
        selected: matchesCommunicatedDemand,
        href:
          baseSidePanelUrl +
          offeringSidePanelBuySideRouteFactory.orderBookIndicationActivity.getUrlPath(),
        testId: xcSelectors.offeringSidePanelCommunicatedDemandLink.testId,
      });
    }

    if (isLinkedOffering || !isOdsFunctionalityEnabled) {
      orderGroup.items.push({
        id: 'internal-demand',
        label: 'Internal Demand',
        selected: matchesInternalDemand,
        href: baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.internalDemand.getUrlPath(),
        testId: xcSelectors.offeringSidePanelInternalDemandLink.testId,
      });
    }

    if (indication.data) {
      orderGroup.items.push({
        id: 'update-history',
        label: 'Update History',
        selected: matchesUpdateHistory,
        href:
          baseSidePanelUrl +
          offeringSidePanelBuySideRouteFactory.orderBookUpdateHistory.getUrlPath({
            cmgEntityKey: cmgEntityKey!,
          }),
        testId: xcSelectors.offeringSidePanelUpdateHistoryLink.testId,
      });
    }

    orderGroup.items.push({
      id: 'compliance',
      label: 'Compliance',
      selected: matchesCompliance,
      href:
        baseSidePanelUrl + offeringSidePanelBuySideRouteFactory.orderBookCompliance.getUrlPath(),
    });

    return orderGroup;
  }, [
    isFiledOffering,
    baseSidePanelUrl,
    isLinkedOffering,
    indication.data,
    cmgEntityKey,
    matchesCommunicatedDemand,
    matchesInternalDemand,
    matchesUpdateHistory,
    matchesCompliance,
    isOdsFunctionalityEnabled,
  ]);
}
